import * as L from 'leaflet';

import { MapFeatureColors, MapObjectAvailablePalette, MapObjectColors } from '../../core/map-object-colors';

/** Saved territory colors stay in the record. Presentation maps them to the
 * shared palette, then separates neighbors in stable ID order. This keeps UI
 * state colors out of normal polygons without a database migration.
 *
 * Automatic neighbor separation needs mutually distinct colors. The wider
 * category palette includes related shades; they remain valid preferred colors.
 */
export const TerritoryPalette: readonly string[] = [
  MapObjectColors.lake, MapObjectColors.mulberry, MapObjectColors.copper,
  MapObjectColors.olive, MapObjectColors.seaPine, MapObjectColors.heather,
  MapObjectColors.slate, MapObjectColors.rose,
];

export const TerritoryFallbackColor: string = MapFeatureColors.territories;

/** Prefer the closest palette color; never mutate the persisted choice. */
export function territoryPaletteColor(color: string | null | undefined): string {
  if (!parseHex(color)) return TerritoryFallbackColor;
  let best = TerritoryFallbackColor;
  let gap = Infinity;
  for (const candidate of MapObjectAvailablePalette) {
    const next = distance(color!, candidate);
    if (next < gap) { best = candidate; gap = next; }
  }
  return best;
}

/**
 * A quieter text colour derived from the boundary colour.
 *
 * Hue is left untouched so the name still belongs to the polygon beside it.
 * Every readable boundary is brought to the same 25% HSL lightness and capped
 * at 38% saturation, then softened with one shared alpha. Neutral saved
 * colours stay neutral rather than being given saturation they did not have.
 */
export function territoryLabelTextColor(color: string): string {
  const rgb = parseHex(color) ?? parseHex(TerritoryFallbackColor)!;
  const { hue, saturation } = rgbToHsl(rgb);
  return `hsl(${Math.round(hue)} ${Math.round(Math.min(saturation, 38))}% 25% / 0.74)`;
}

/**
 * How far apart two colours have to be to read as two colours.
 *
 * On the scale below (0 to about 765) the pairs that prompted this sit well
 * under it - purple against deep purple is 80, teal against darker teal 96,
 * orange against deep orange 70. Blue against indigo is 150 and still reads as
 * one colour on a map at a glance, so the line is drawn above it.
 */
export const TerritoryColorDistance = 120;

/** A patch, as colouring needs to see it. */
export interface TerritoryColorInput {
  readonly id: number;
  /** The colour on the record, or null when it never got one. */
  readonly color: string | null;
  /** Where it is. Null when the boundary would not parse - such a patch is
   *  drawn in its preferred palette color but cannot be anyone's neighbour. */
  readonly bounds: L.LatLngBounds | null;
}

/**
 * A colour per territory id, with no two neighbours too close to tell apart.
 *
 * Greedy, in id order, so the answer is the same on every render and does not
 * depend on the order rows arrived in: an older patch keeps the colour it was
 * mapped to, and the newer one beside it is the one that moves. Sorting by id also
 * means adding a patch cannot repaint the map behind it.
 */
export function territoryDisplayColors(
  patches: readonly TerritoryColorInput[],
): Map<number, string> {
  const ordered = [...patches].sort((a, b) => a.id - b.id);
  const neighbours = adjacency(ordered);
  const chosen = new Map<number, string>();

  for (const patch of ordered) {
    const taken = (neighbours.get(patch.id) ?? [])
      .map(id => chosen.get(id))
      .filter((color): color is string => color !== undefined);

    const preferred = territoryPaletteColor(patch.color);
    if (taken.every(other => farEnough(preferred, other))) {
      chosen.set(patch.id, preferred);
      continue;
    }
    chosen.set(patch.id, pickApartFrom(taken));
  }

  return chosen;
}

/**
 * Which patches are near enough that a reader would compare their colours.
 *
 * Bounding boxes, padded a little. Two patches that share a border have boxes
 * that overlap; two on opposite sides of the county do not. It says yes to
 * some pairs that do not actually touch - one patch's box can cover another it
 * merely surrounds - and that is the direction to be wrong in: an extra pair
 * kept apart costs a colour, a missed pair costs the border between them.
 *
 * The padding covers patches drawn before boundary snapping existed, whose
 * shared edge has a hair of daylight in it.
 */
function adjacency(
  patches: readonly TerritoryColorInput[],
): Map<number, number[]> {
  const near = new Map<number, number[]>();
  for (const patch of patches) near.set(patch.id, []);

  for (let i = 0; i < patches.length; i++) {
    const a = patches[i];
    if (!a.bounds) continue;
    const padded = a.bounds.pad(0.02);
    for (let j = i + 1; j < patches.length; j++) {
      const b = patches[j];
      if (!b.bounds || !padded.intersects(b.bounds)) continue;
      near.get(a.id)!.push(b.id);
      near.get(b.id)!.push(a.id);
    }
  }

  return near;
}

/** The first palette colour far enough from all of these; failing that, the
 *  one that is furthest from the nearest of them. */
function pickApartFrom(taken: readonly string[]): string {
  let best = TerritoryFallbackColor;
  let bestGap = -1;

  for (const candidate of TerritoryPalette) {
    const gap = taken.length
      ? Math.min(...taken.map(other => distance(candidate, other)))
      : Infinity;
    if (gap >= TerritoryColorDistance) return candidate;
    if (gap > bestGap) { best = candidate; bestGap = gap; }
  }

  return best;
}

function farEnough(a: string, b: string): boolean {
  return distance(a, b) >= TerritoryColorDistance;
}

/**
 * How different two colours look, near enough.
 *
 * The "redmean" approximation: plain Euclidean distance in RGB calls #2e7d32
 * and #26a69a further apart than the eye does, and weighting the channels by
 * where the pair sits on the red axis costs three lines and fixes it. Not a
 * colour space conversion - this decides whether two map fills are tellable
 * apart, not whether a print matches.
 */
export function distance(a: string, b: string): number {
  const x = parseHex(a);
  const y = parseHex(b);
  // An unreadable colour is not evidence of anything, so it clashes with
  // nothing and the patch keeps whatever it has.
  if (!x || !y) return Infinity;

  const redMean = (x.r + y.r) / 2;
  const dr = x.r - y.r;
  const dg = x.g - y.g;
  const db = x.b - y.b;
  return Math.sqrt(
    ((512 + redMean) * dr * dr) / 256
    + 4 * dg * dg
    + ((767 - redMean) * db * db) / 256);
}

interface Rgb { r: number; g: number; b: number }

function rgbToHsl(rgb: Rgb): { hue: number; saturation: number } {
  const red = rgb.r / 255;
  const green = rgb.g / 255;
  const blue = rgb.b / 255;
  const maximum = Math.max(red, green, blue);
  const minimum = Math.min(red, green, blue);
  const delta = maximum - minimum;
  const lightness = (maximum + minimum) / 2;
  const saturation = delta === 0
    ? 0
    : delta / (1 - Math.abs(2 * lightness - 1));
  let hue = 0;

  if (delta !== 0) {
    if (maximum === red) hue = 60 * (((green - blue) / delta) % 6);
    else if (maximum === green) hue = 60 * ((blue - red) / delta + 2);
    else hue = 60 * ((red - green) / delta + 4);
  }

  return {
    hue: hue < 0 ? hue + 360 : hue,
    saturation: saturation * 100,
  };
}

/** `#rgb` and `#rrggbb`, the two forms the colour input can produce. Anything
 *  else is null: a colour nobody can read is not one to reason about. */
function parseHex(color: string | null | undefined): Rgb | null {
  if (!color) return null;
  const hex = color.trim().replace(/^#/, '');
  const full = hex.length === 3
    ? hex.split('').map(c => c + c).join('')
    : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const value = parseInt(full, 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}
